import {
  AKTION_10,
  App,
  convertProduktNameToTaasProduktName,
  convertToBffLaufzeit,
  DIPLOMATEN_CODE,
  FEVorvertragsnummer,
  Gefahr,
  getSpartenTypeByApp,
  Produkt,
  Produktlinie,
  TaasInfo,
  TIER_10,
  Vertragsoptionen,
  VnWechselAngaben,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import {
  BausteinAuswahlStatus,
  SpartenType,
  TaasGegeData,
  TaasJhpData,
  TaasPhData,
  TaasRequestModel,
  TaasRsData,
  TaasRsVmData,
  TaasTarifrisikoschluesselGEGE,
  TaasTarifrisikoschluesselJHP,
  TaasTarifrisikoschluesselPH,
  TaasTarifrisikoschluesselRS,
  TaasTarifrisikoschluesselTHH,
  TaasTarifrisikoschluesselTKV,
  TaasTarifrisikoschluesselTKV_V2,
  TaasTarifrisikoschluesselURI,
  TaasTarifrisikoschluesselVHV,
  TaasTarifrisikoschluesselVHVV2,
  TaasTarifrisikoschluesselVWG,
  TaasTarifrisikoschluesselVWGV2,
  TaasThhHundData,
  TaasThhPferdData,
  TaasTkvV1Data,
  TaasTkvV2Data,
  TaasUriData,
  TaasVhvData,
  TaasVhvV2Data,
  TaasVnWechsel,
  TaasVwgV1Data,
  TaasVwgV2Data,
  Tarifbereich,
  TarifierungsArt,
} from '@ps20/bff-models';
import hash from 'object-hash';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { isNeugeschaeft } from '@ps20-frontend/shop-integration-redux';
import {
  TaasVertragsoptionen,
  TaasVertragsoptionenFeatureToggles,
  WeitererVertrag,
} from './taasVertragsoptionen';

export type TaasData =
  | TaasVwgV1Data
  | TaasVwgV2Data
  | TaasVhvData
  | TaasVhvV2Data
  | TaasPhData
  | TaasRsData
  | TaasRsVmData
  | TaasThhPferdData
  | TaasThhHundData
  | TaasTkvV1Data
  | TaasTkvV2Data
  | TaasUriData
  | TaasJhpData
  | TaasGegeData;

type TaasTarifrisikoschluessel =
  | TaasTarifrisikoschluesselVWG
  | TaasTarifrisikoschluesselVWGV2
  | TaasTarifrisikoschluesselVHV
  | TaasTarifrisikoschluesselVHVV2
  | TaasTarifrisikoschluesselPH
  | TaasTarifrisikoschluesselRS
  | TaasTarifrisikoschluesselTHH
  | TaasTarifrisikoschluesselTKV
  | TaasTarifrisikoschluesselTKV_V2
  | TaasTarifrisikoschluesselURI
  | TaasTarifrisikoschluesselJHP
  | TaasTarifrisikoschluesselGEGE;

export function buildBasicSalesProductsForStrecke<T extends TaasTarifrisikoschluessel>(
  produkt: Produkt,
  convertTaasBausteine: (baustein: string) => T,
): { [index: string]: BausteinAuswahlStatus<T>[] } {
  const produktlinien: Produktlinie[] = produkt.produktlinien;
  const taasBausteine: { [index: string]: BausteinAuswahlStatus<T>[] } = {};

  for (const produktlinie of produktlinien) {
    let selectedGefahren: Gefahr[] = [];
    if (produktlinie.gefahren) {
      selectedGefahren = produktlinie.gefahren.filter(gefahr => gefahr.isSelected);
    }
    const key = convertProduktNameToTaasProduktName(produktlinie.produktName);
    taasBausteine[key] = buildBausteinAuswahlStatus(
      selectedGefahren,
      produktlinie.zusatzoptionen,
      convertTaasBausteine,
    );
  }
  return taasBausteine;
}

export function buildBasicSalesProductsForShop<T extends TaasTarifrisikoschluessel>(
  produkt: Produkt,
  convertTaasBausteine: (baustein: string) => T,
): { [index: string]: BausteinAuswahlStatus<T>[] } {
  const produktlinien: Produktlinie[] = produkt.produktlinien.filter(
    produktlinie => produktlinie.selected,
  );
  const taasBausteine: { [index: string]: BausteinAuswahlStatus<T>[] } = {};

  for (const produktlinie of produktlinien) {
    const selectedZusatzoptionen: ZusatzoptionItem[] = produktlinie.zusatzoptionen.filter(
      zusatzoption => zusatzoption.isSelected,
    );
    let selectedGefahren: Gefahr[];
    if (produktlinie.gefahren) {
      selectedGefahren = produktlinie.gefahren.filter(gefahr => gefahr.isSelected);
    }
    const key = convertProduktNameToTaasProduktName(produktlinie.produktName);
    taasBausteine[key] = buildBausteinAuswahlStatus(
      selectedGefahren,
      selectedZusatzoptionen,
      convertTaasBausteine,
    );
  }
  return taasBausteine;
}

function buildBausteinAuswahlStatus<T extends TaasTarifrisikoschluessel>(
  gefahren: Gefahr[],
  zusatzoptionen: ZusatzoptionItem[],
  convertTaasBausteine: (baustein: string) => T,
): BausteinAuswahlStatus<T>[] {
  const bausteinAuswahlStatusArray: BausteinAuswahlStatus<T>[] = [];
  for (const gefahr of gefahren) {
    bausteinAuswahlStatusArray.push({
      schluessel: convertTaasBausteine(gefahr.name),
      ausgewaehlt: gefahr.isSelected,
      isGefahr: true,
      isInklusiv: false,
      deltafaktor: gefahr.deltafaktor,
      anzahlVorschaeden: gefahr.anzahlVorschaeden,
      plzFaktor: gefahr.plzFaktor,
      bausteinAnpassungsfaktor: gefahr.bausteinAnpassungsfaktor,
    });
  }
  for (const zusatzoption of zusatzoptionen) {
    bausteinAuswahlStatusArray.push({
      schluessel: convertTaasBausteine(zusatzoption.name),
      ausgewaehlt: zusatzoption.isSelected,
      isGefahr: false,
      isInklusiv: zusatzoption.isInclusive,
      deltafaktor: zusatzoption.deltafaktor,
      anzahlVorschaeden: zusatzoption.anzahlVorschaeden,
      plzFaktor: zusatzoption.plzFaktor,
      bausteinAnpassungsfaktor: zusatzoption.bausteinAnpassungsfaktor,
    });
  }
  return bausteinAuswahlStatusArray;
}

export function buildTaasRequestModelForStrecke<T extends TaasData>(
  vertragsoptionen: TaasVertragsoptionen,
  spartenDaten: T,
  tarifBereich: Tarifbereich,
  vnWechselAngaben?: VnWechselAngaben,
  taasInfo?: TaasInfo,
): TaasRequestModel<T> {
  const taasRequestModel: TaasRequestModel<T> = buildGeneralTaasRequestModel(
    spartenDaten,
    vertragsoptionen,
    tarifBereich,
    TarifierungsArt.ANTRAGSSTRECKE,
    vnWechselAngaben,
  );

  taasRequestModel.zahlweise = vertragsoptionen.zahlungsweise;
  taasRequestModel.anzahlKombiVertraege = 0;
  taasRequestModel.zuschlagRisiko = taasInfo?.zuschlagRisiko || 0;
  taasRequestModel.zuschlagRisikoVorvertrag = taasInfo?.zuschlagRisikoVorvertrag;
  return taasRequestModel;
}

export function buildTaasRequestModelForShop<T extends TaasData>(
  spartenDaten: T,
  newHashedTaasRequest: string,
  vertragsoptionen: TaasVertragsoptionen,
  taasInfo: TaasInfo,
  tarifBereich: Tarifbereich,
  vnWechselAngaben?: VnWechselAngaben,
): TaasRequestModel<T> {
  const taasRequestModel: TaasRequestModel<T> = buildGeneralTaasRequestModel(
    spartenDaten,
    vertragsoptionen,
    tarifBereich,
    TarifierungsArt.SHOP,
    vnWechselAngaben,
  );
  taasRequestModel.selbstbeteiligung = vertragsoptionen.selbstbeteiligung;
  taasRequestModel.beibehalten = vertragsoptionen.beibehalten;
  taasRequestModel.wirksamkeitsbeginn = taasInfo.wirksamkeitsBeginn;
  taasRequestModel.isAktion10 = vertragsoptionen.isAktion10Assigned;
  taasRequestModel.isTier10 = vertragsoptionen.isTier10Assigned;
  taasRequestModel.aktionsnachlass = getAktionsnachlass(taasInfo, vertragsoptionen);
  const nachlassRbd = getCorrectNachlassRbd(taasInfo, newHashedTaasRequest, vertragsoptionen);
  const nachlassTb = getCorrectNachlassTb(taasInfo, newHashedTaasRequest, vertragsoptionen);
  const nachlaesseFaktor = getCorrectNachlaesseFaktor(taasInfo, vertragsoptionen);
  const zuschlaegeFaktor = getCorrectZuschlaegeFaktor(taasInfo);
  const anspassungsFaktor = getCorrectAnpassungsFaktor(taasInfo, vertragsoptionen);

  if (nachlassRbd) {
    taasRequestModel.nachlassRbd = nachlassRbd;
  }

  if (nachlassTb) {
    taasRequestModel.nachlassTb = nachlassTb;
  }

  if (nachlaesseFaktor) {
    taasRequestModel.nachlaesseFaktor = nachlaesseFaktor;
  }

  if (zuschlaegeFaktor) {
    taasRequestModel.zuschlaegeFaktor = zuschlaegeFaktor;
  }

  if (anspassungsFaktor) {
    taasRequestModel.anpassungsfaktor = anspassungsFaktor;
  }
  taasRequestModel.vorteilsrabatt = taasInfo.vorteilsrabatt || 0;
  taasRequestModel.zuschlagRisiko = taasInfo.zuschlagRisiko || 0;
  taasRequestModel.zuschlagRisikoVorvertrag = taasInfo.zuschlagRisikoVorvertrag;
  return taasRequestModel;
}

function buildGeneralTaasRequestModel<T extends TaasData>(
  spartenDaten: T,
  vertragsoptionen: TaasVertragsoptionen,
  tarifBereich: Tarifbereich,
  tarifierungsArt: TarifierungsArt,
  vnWechselAngaben?: VnWechselAngaben,
): TaasRequestModel<T> {
  const beginnDatum = dayjs(vertragsoptionen.beginndatum, DATEFORMAT_LOCAL_STATE).format(
    DATEFORMAT_SHORT_ISO,
  );
  const aenderungDatum = dayjs(vertragsoptionen.aenderungdatum, DATEFORMAT_LOCAL_STATE).format(
    DATEFORMAT_SHORT_ISO,
  );
  const angebotsdatum = vertragsoptionen.angebotsdatum;
  const ablaufDatum = getCorrectAblaufdatum(vertragsoptionen, beginnDatum);

  const taasRequestModel: TaasRequestModel<T> = {
    beginndatum: beginnDatum,
    aenderungdatum: aenderungDatum,
    ablaufdatum: ablaufDatum,
    angebotsdatum: angebotsdatum,
    taasSparte: spartenDaten,
    zahlungsart: vertragsoptionen.zahlungsart,
    tarifbereich: tarifBereich,
    tarifierungsArt,
    vorvertragsNummern: vertragsoptionen.vorvertragsNummern,
    weitereVertraege: vertragsoptionen.weitereVertraege,
    vorvertragGekuendigt: vertragsoptionen.vorvertragGekuendigt,
    isDiplomatengeschaeft: vertragsoptionen.isDiplomatengeschaeft,
    laufzeit: convertToBffLaufzeit(vertragsoptionen.vertragslaufzeit),
  };

  if (vnWechselAngaben !== undefined && vnWechselAngaben.angabenListe?.length > 0) {
    taasRequestModel.vnWechsel = getVnWechsel(vnWechselAngaben);
  }

  return taasRequestModel;
}

function getCorrectAblaufdatum(
  vertragsoptionen: TaasVertragsoptionen,
  beginndatum: string,
): string {
  if (vertragsoptionen.ablaufdatum) {
    return dayjs(vertragsoptionen.ablaufdatum, DATEFORMAT_LOCAL_STATE).format(DATEFORMAT_SHORT_ISO);
  } else {
    return calculateAblaufdatum(beginndatum, vertragsoptionen.vertragslaufzeit);
  }
}

export function calculateAblaufdatum(beginnCalculated: string, vertragslaufzeit: number): string {
  return dayjs(beginnCalculated, DATEFORMAT_SHORT_ISO)
    .add(vertragslaufzeit ?? 1, 'year')
    .format(DATEFORMAT_SHORT_ISO);
}

export function getCorrectNachlassRbd(
  taasInfo: TaasInfo,
  newHashedTaasRequest: string,
  vertragsoptionen: TaasVertragsoptionen,
): number {
  if (isSameHash(taasInfo, newHashedTaasRequest) || vertragsoptionen.beibehalten) {
    return taasInfo.rbdNachlass;
  }
  return null;
}

export function getCorrectNachlassTb(
  taasInfo: TaasInfo,
  newHashedTaasRequest: string,
  vertragsoptionen: TaasVertragsoptionen,
): number {
  if (isSameHash(taasInfo, newHashedTaasRequest) || vertragsoptionen.beibehalten) {
    return taasInfo.tbNachlass;
  }
  return null;
}

export function isSameHash(taasInfo: TaasInfo, newHashedTaasRequest: string): boolean {
  return taasInfo.hashedTaasRequest === newHashedTaasRequest;
}

function getCorrectNachlaesseFaktor(
  taasInfo: TaasInfo,
  vertragsoptionen: TaasVertragsoptionen,
): number {
  if (taasInfo.nachlaesseFaktor && vertragsoptionen.beibehalten) {
    return taasInfo.nachlaesseFaktor;
  }
  return null;
}

function getCorrectZuschlaegeFaktor( taasInfo: TaasInfo): number {
  return taasInfo.zuschlaegeFaktor ?? null;
}

function getCorrectAnpassungsFaktor(
  taasInfo: TaasInfo,
  vertragsoptionen: TaasVertragsoptionen,
): number {
  if (taasInfo.anpassungsFaktor && vertragsoptionen.beibehalten) {
    return taasInfo.anpassungsFaktor;
  }
  return null;
}

function getAktionsnachlass(taasInfo: TaasInfo, vertragsoptionen: TaasVertragsoptionen): number {
  if (taasInfo.aktionsnachlass && vertragsoptionen.beibehalten) {
    return taasInfo.aktionsnachlass;
  }
  return undefined;
}

function getVnWechsel(vnWechselAngaben: VnWechselAngaben): TaasVnWechsel[] {
  const taasVnWechsel: TaasVnWechsel[] = new Array<TaasVnWechsel>();
  vnWechselAngaben.angabenListe.forEach(angaben => {
    angaben.fremdVNVorvertraege?.forEach(fremdVnVorvertrag => {
      if (fremdVnVorvertrag && fremdVnVorvertrag.vnWechselGrund?.length > 0) {
        taasVnWechsel.push({
          vorvertragsNummer: fremdVnVorvertrag.vorvertragsnummer?.vertragsnummer,
          wechselGrund: fremdVnVorvertrag.vnWechselGrund[0]?.value,
        });
      }
    });
  });
  return taasVnWechsel;
}

export function createHashedTaasData(spartenDaten: Partial<TaasData>): string {
  if (spartenDaten !== undefined) {
    return createObjectHash(spartenDaten);
  } else {
    return '';
  }
}

function createObjectHash(spartenDaten: Partial<TaasData>): string {
  const reducedSpartenDaten = reduceSpartenDaten(spartenDaten);
  return hash(reducedSpartenDaten);
}

function reduceSpartenDaten(spartenDaten: Partial<TaasData>): Partial<TaasData> {
  return Object.entries(spartenDaten)
    .map(([key, value]) => [
      key,
      value && typeof value === 'object' ? reduceSpartenDaten(value) : value,
    ])
    .reduce((acc, [key, value]) => {
      if (value != null) {
        (acc as Record<string, unknown>)[key] = value;
      }
      return acc;
    }, {} as Partial<TaasData>);
}

export function getUrlForApp(appId: App): string {
  const urlTemplate = '/privatschutz/api/taas/';
  const urlMappings = {
    [App.VWG]: urlTemplate + 'vwg',
    [App.VWG_V2]: urlTemplate + 'vwg',
    [App.VHV]: urlTemplate + 'vhv',
    [App.VHV_V2]: urlTemplate + 'vhv',
    [App.PH]: urlTemplate + 'ph',
    [App.RS]: urlTemplate + 'rs',
    [App.RS_VERMIETUNG]: urlTemplate + 'rs',
    [App.THH_HUND]: urlTemplate + 'thh',
    [App.THH_PFERD]: urlTemplate + 'thh',
    [App.TKV]: urlTemplate + 'tkv',
    [App.TKV_V2]: urlTemplate + 'tkv',
    [App.JHP]: urlTemplate + 'jhp',
    [App.UNF]: urlTemplate + 'uri',
    [App.GEGE]: urlTemplate + 'gege',
  };
  return (urlMappings as Record<string, string>)[appId];
}

export function getTaasVertragsoptionen(
  vertragsoptionen: Vertragsoptionen,
  spartenType: SpartenType = undefined,
  featureToggles: TaasVertragsoptionenFeatureToggles = {},
): TaasVertragsoptionen {
  const { aaWorldTkvFT = false, aktion10FT = false, tier10FT = false } = featureToggles;
  return {
    vertragslaufzeit: vertragsoptionen.ausgewaehltevertragslaufzeit,
    zahlungsweise: vertragsoptionen.zahlungsweise,
    zahlungsart: vertragsoptionen.zahlungsart,
    selbstbeteiligung: vertragsoptionen.selbstbeteiligung,
    beginndatum: vertragsoptionen.beginnDatum,
    aenderungdatum: vertragsoptionen.aenderungdatum,
    ablaufdatum: vertragsoptionen.ablaufDatum,
    angebotsdatum: vertragsoptionen.angebotsdatum,
    vorvertragGekuendigt: vertragsoptionen.vorvertragGekuendigt,
    vorvertragsNummern:
      vertragsoptionen.vorvertragsNummern &&
      vertragsoptionen.vorvertragsNummern.map(
        vorvertragsNummer => vorvertragsNummer.vertragsnummer,
      ),
    weitereVertraege: mapWeiterenVertrag(vertragsoptionen.weitereVertraege),
    beibehalten: vertragsoptionen.beibehalten,
    isDiplomatengeschaeft:
      aaWorldTkvFT && spartenType === SpartenType.TIERKRANKEN
        ? false
        : vertragsoptionen.selectedKooperation === DIPLOMATEN_CODE,
    isAktion10Assigned: aktion10FT && checkIfAktion10Possible(vertragsoptionen, spartenType),
    isTier10Assigned: tier10FT && checkIfTier10Possible(vertragsoptionen, spartenType),
  };
}
function checkIfAktion10Possible(
  vertragsoptionen: Vertragsoptionen,
  spartenType: SpartenType,
): boolean {
  return (
    vertragsoptionen.selectedKooperation === AKTION_10 &&
    isNeugeschaeft(vertragsoptionen) &&
    SpartenType.WOHNGEBAEUDE !== spartenType &&
    SpartenType.WOHNGEBAEUDE_V2 !== spartenType
  );
}
function checkIfTier10Possible(
  vertragsoptionen: Vertragsoptionen,
  spartenType: SpartenType,
): boolean {
  return (
    vertragsoptionen.selectedKooperation === TIER_10 &&
    isNeugeschaeft(vertragsoptionen) &&
    (SpartenType.TIERHALTERHAFTPFLICHT_HUND === spartenType ||
      SpartenType.TIERHALTERHAFTPFLICHT_PFERD === spartenType)
  );
}

export function parseDatum(datum: string): string {
  return dayjs(datum, DATEFORMAT_LOCAL_STATE).format(DATEFORMAT_SHORT_ISO);
}

export function mapWeiterenVertrag(weitereVertraege: FEVorvertragsnummer[]): WeitererVertrag[] {
  return (
    weitereVertraege &&
    weitereVertraege.map(
      value =>
        ({
          vorvertragsnummer: value.vertragsnummer,
          spartenType: getSpartenTypeByApp(value.produktIds[0]),
        }) as WeitererVertrag,
    )
  );
}
